import React, { useState, useEffect } from "react";
import { useForm, ValidationError } from "@formspree/react";
import { motion } from 'framer-motion';
import { Mail, MapPin, Users, Newspaper, ExternalLink } from 'lucide-react';
import { FaInstagram, FaLinkedin } from 'react-icons/fa';


const Contact = () => {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [state, handleSubmit] = useForm("mpwarjnl");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  useEffect(() => {
    let successTimer, errorTimer;
    if (state.succeeded) {
      setShowSuccessMessage(true);
      successTimer = setTimeout(() => setShowSuccessMessage(false), 5000);
      resetForm();
    } else if (state.errors && state.errors.length > 0) {
      setShowErrorMessage(true);
      errorTimer = setTimeout(() => setShowErrorMessage(false), 5000);
    }
    return () => {
      clearTimeout(successTimer);
      clearTimeout(errorTimer);
    };
  }, [state.succeeded, state.errors]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormState({
      name: "",
      email: "",
      subject: "",
      message: ""
    });
  };

  // Ways to get involved options
  const involvementOptions = [
    {
      icon: <Users size={24} />,
      title: "Volunteer",
      description: "Join our team of dedicated volunteers helping refugee students through tutoring, mentorship, and event support."
    },
    {
      icon: <ExternalLink size={24} />,
      title: "Partner With Us",
      description: "Organizations can partner with us to create meaningful impact through collaborative initiatives and shared resources."
    },
    {
      icon: <Newspaper size={24} />,
      title: "Spread Awareness",
      description: "Share our mission on social media, in your community, or at your school to help amplify refugee voices."
    }
  ];


  return (
    <section id="contact" className="py-16 bg-lightTeal relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-primary opacity-5"></div>
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-cta opacity-5"></div>
      
      <div className="max-w-6xl mx-auto px-6 md:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="font-display text-h2 text-secondary mb-2">GET INVOLVED</h2>
          <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
            Join our mission to support refugee students worldwide
          </p>
          <div className="w-16 h-1 bg-cta mx-auto"></div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          {/* Contact Form */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="font-display text-h3 text-secondary mb-4">Send Us a Message</h3>
              
              {showSuccessMessage && (
                <div className="mb-6 p-4 bg-primary/10 text-primary rounded-lg border border-primary/20">
                  Thank you for your message! We'll get back to you soon.
                </div>
              )}
              
              {showErrorMessage && (
                <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
                  There was a problem sending your message. Please try again.
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-textPrimary text-sm font-medium mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      required
                      disabled={state.submitting}
                    />
                  </div>
                  <div>
                    <label className="block text-textPrimary text-sm font-medium mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formState.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      required
                      disabled={state.submitting}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-textPrimary text-sm font-medium mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formState.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    required
                    disabled={state.submitting}
                  />
                </div>
                
                <div>
                  <label className="block text-textPrimary text-sm font-medium mb-2">
                    Your Message
                  </label>
                  <textarea
                    name="message"
                    value={formState.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    required
                    disabled={state.submitting}
                  />
                </div>
                
                <ValidationError prefix="Email" field="email" errors={state.errors} />
                <ValidationError prefix="Message" field="message" errors={state.errors} />
                
                <motion.button
                  type="submit"
                  disabled={state.submitting}
                  className="w-full bg-primary text-white py-3 px-8 rounded-lg text-button font-body transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {state.submitting ? "Sending..." : "Send Message"}
                </motion.button>
              </form>
            </motion.div>
          </div>
          
          {/* Contact Info */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <motion.div 
              className="bg-secondary text-white rounded-xl p-8 mb-6 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="relative z-10">
                <h3 className="font-display text-h3 text-white mb-6">Contact Information</h3>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4">
                      <Mail size={18} className="text-white" />
                    </div>
                    <a href="mailto:hello@studentsforrefugees.org" className="text-white hover:underline">
                      hello@studentsforrefugees.org
                    </a>
                  </div>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4">
                      <MapPin size={18} className="text-white" />
                    </div>
                    <span>Texas, USA</span>
                  </div>
                </div>
                
                <h4 className="font-display text-white text-lg mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                  <a 
                    href="https://www.instagram.com/sfrnational/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
                  >
                    <FaInstagram size={18} />
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/studentsforrefugees" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
                  >
                    <FaLinkedin size={18} />
                  </a>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute bottom-0 right-0 w-32 h-32 rounded-full bg-white opacity-5 translate-x-10 translate-y-10"></div>
              <div className="absolute top-0 left-0 w-20 h-20 rounded-full bg-white opacity-5 -translate-x-10 -translate-y-10"></div>
            </motion.div>
          </div>
        </div>
        
        {/* Ways to Get Involved */}
        <div>
          <h3 className="font-display text-h3 text-secondary text-center mb-10">Other Ways to Get Involved</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {involvementOptions.map((option, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-xl p-6 shadow-md"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 text-primary">
                  {option.icon}
                </div>
                <h4 className="font-display text-xl text-secondary mb-2">{option.title}</h4>
                <p className="text-textPrimary">{option.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;